import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, X, Copy } from "lucide-react";
import { api, formatApiError, formatPrice } from "@/lib/api";
import { Header } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = {
  title: "", short_description: "", long_description: "", price: "",
  thumbnail_url: "", ott_purchase_url: "", vimeo_video_id: "", ott_product_id: "",
  mux_playback_id: "", mux_playback_policy: "signed",
};

const WEBHOOK_URL = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/vimeo`;

function VideoForm({ initial, onSaved, onCancel }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Le titre est requis");
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price || 0) };
      if (initial?.id) {
        await api.put(`/admin/videos/${initial.id}`, payload);
        toast.success("Vidéo mise à jour");
      } else {
        await api.post("/admin/videos", payload);
        toast.success("Vidéo ajoutée au catalogue");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const field = (id, label, key, props = {}) => (
    <div>
      <Label htmlFor={id} className="text-xs uppercase tracking-widest text-[#8F8F8F]">{label}</Label>
      <Input id={id} data-testid={`video-form-${id}`} value={form[key]} onChange={set(key)}
        className="mt-2 rounded-none border-white/15 bg-transparent text-[#F5F3EE] placeholder:text-[#5A5A5A]" {...props} />
    </div>
  );

  return (
    <form onSubmit={submit} className="bg-[#141414] border border-white/10 p-8 space-y-5" data-testid="video-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl text-[#F5F3EE]">{initial?.id ? "Modifier la vidéo" : "Nouvelle vidéo"}</h3>
        <button type="button" data-testid="video-form-cancel" onClick={onCancel} className="text-[#8F8F8F] hover:text-[#F5F3EE]">
          <X className="w-5 h-5" />
        </button>
      </div>
      {field("title", "Titre *", "title", { placeholder: "Titre de la vidéo" })}
      {field("short-desc", "Description courte", "short_description", { placeholder: "Une phrase d'accroche" })}
      <div>
        <Label htmlFor="long-desc" className="text-xs uppercase tracking-widest text-[#8F8F8F]">Description longue</Label>
        <Textarea id="long-desc" data-testid="video-form-long-desc" value={form.long_description}
          onChange={set("long_description")} rows={4} className="mt-2 rounded-none border-white/15 bg-transparent text-[#F5F3EE] placeholder:text-[#5A5A5A]" />
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        {field("price", "Prix affiché (€)", "price", { type: "number", step: "0.01", min: "0", placeholder: "19.99" })}
        {field("thumbnail", "URL de la miniature", "thumbnail_url", { placeholder: "https://..." })}
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        {field("mux-playback-id", "ID de lecture Mux (playback ID)", "mux_playback_id", { placeholder: "abc123XYZ..." })}
        <div>
          <Label htmlFor="mux-policy" className="text-xs uppercase tracking-widest text-[#8F8F8F]">Politique de lecture Mux</Label>
          <select id="mux-policy" data-testid="video-form-mux-policy" value={form.mux_playback_policy}
            onChange={set("mux_playback_policy")}
            className="mt-2 w-full h-9 border border-white/15 bg-[#141414] px-3 text-sm text-[#F5F3EE]">
            <option value="signed">Signée (sécurisée, recommandé)</option>
            <option value="public">Publique (sans jeton)</option>
          </select>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        {field("vimeo-id", "ID vidéo Vimeo (lecteur)", "vimeo_video_id", { placeholder: "123456789" })}
        {field("product-id", "ID produit OTT (webhook, optionnel)", "ott_product_id", { placeholder: "15275" })}
      </div>
      <button type="submit" data-testid="video-form-submit" disabled={saving}
        className="w-full bg-[#D4AF37] text-[#0A0A0A] font-semibold py-3.5 text-sm uppercase tracking-widest hover:bg-[#C9A227] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
        {saving && <Loader2 className="w-4 h-4 animate-spin" />} {initial?.id ? "Enregistrer" : "Ajouter la vidéo"}
      </button>
    </form>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("videos");
  const [videos, setVideos] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [editing, setEditing] = useState(null);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantVideoId, setGrantVideoId] = useState("");
  const [promos, setPromos] = useState([]);
  const [promoForm, setPromoForm] = useState({ code: "", discount_type: "percent", value: "", max_uses: "" });

  const load = useCallback(() => {
    api.get("/admin/videos").then((r) => setVideos(r.data)).catch(() => {});
    api.get("/admin/purchases").then((r) => setPurchases(r.data)).catch(() => {});
    api.get("/admin/promos").then((r) => setPromos(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user, load]);

  if (user === undefined) {
    return <div className="min-h-screen bg-luxe flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" /></div>;
  }
  if (!user || user.role !== "admin") {
    return (
      <div className="bg-luxe min-h-screen">
        <Header />
        <div className="max-w-md mx-auto px-6 pt-40 text-center" data-testid="admin-denied">
          <h1 className="font-display text-2xl text-[#F5F3EE]">Accès réservé</h1>
          <p className="mt-4 text-sm text-[#8F8F8F]">Cette page est réservée à l'administrateur.</p>
          <Link to="/connexion" data-testid="admin-go-login" className="inline-block mt-8 bg-[#D4AF37] text-[#0A0A0A] font-semibold px-8 py-3.5 text-sm uppercase tracking-widest hover:bg-[#C9A227] transition-colors">
            Se connecter
          </Link>
        </div>
      </div>
    );
  }

  const deleteVideo = async (id) => {
    if (!window.confirm("Supprimer cette vidéo et tous les accès associés ?")) return;
    await api.delete(`/admin/videos/${id}`);
    toast.success("Vidéo supprimée");
    load();
  };

  const grantAccess = async (e) => {
    e.preventDefault();
    if (!grantEmail || !grantVideoId) return toast.error("Email et vidéo requis");
    try {
      await api.post("/admin/grant-access", { email: grantEmail, video_id: grantVideoId });
      toast.success("Accès accordé");
      setGrantEmail(""); setGrantVideoId("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Révoquer cet accès ?")) return;
    await api.delete(`/admin/purchases/${id}`);
    toast.success("Accès révoqué");
    load();
  };

  const createPromo = async (e) => {
    e.preventDefault();
    if (!promoForm.code.trim() || !promoForm.value) return toast.error("Code et valeur requis");
    try {
      await api.post("/admin/promos", {
        code: promoForm.code,
        discount_type: promoForm.discount_type,
        value: parseFloat(promoForm.value),
        max_uses: promoForm.max_uses ? parseInt(promoForm.max_uses, 10) : null,
      });
      toast.success("Code promo créé");
      setPromoForm({ code: "", discount_type: "percent", value: "", max_uses: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const togglePromo = async (id) => {
    await api.put(`/admin/promos/${id}/toggle`);
    load();
  };

  const deletePromo = async (id) => {
    if (!window.confirm("Supprimer ce code promo ?")) return;
    await api.delete(`/admin/promos/${id}`);
    toast.success("Code supprimé");
    load();
  };

  return (
    <div className="bg-luxe min-h-screen">
      <Header />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-32 pb-24">
        <h1 className="font-display text-3xl text-[#F5F3EE]">Administration</h1>

        <div className="mt-6 bg-[#141414] border border-white/10 p-5 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <p className="text-xs uppercase tracking-widest text-[#8F8F8F]">Webhook Vimeo OTT à configurer</p>
            <code data-testid="webhook-url" className="text-sm text-[#D4AF37] break-all">{WEBHOOK_URL}</code>
          </div>
          <button
            data-testid="copy-webhook-button"
            onClick={() => { navigator.clipboard.writeText(WEBHOOK_URL); toast.success("URL copiée"); }}
            className="flex items-center gap-2 border border-white/15 px-4 py-2 text-xs uppercase tracking-widest hover:border-[#D4AF37]/60 hover:shadow-[0_0_28px_rgba(212,175,55,0.14)] transition-[border-color,box-shadow] duration-300"
          >
            <Copy className="w-3.5 h-3.5" /> Copier
          </button>
        </div>

        <div className="mt-10 flex gap-6 border-b border-white/10">
          {[["videos", "Vidéos"], ["access", "Achats & accès"], ["promos", "Codes promo"]].map(([k, label]) => (
            <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
              className={`pb-3 text-sm uppercase tracking-widest transition-colors ${tab === k ? "text-[#D4AF37] border-b-2 border-[#D4AF37]" : "text-[#8F8F8F] hover:text-[#D4AF37]"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "videos" && (
          <div className="mt-10 space-y-8">
            {editing !== null ? (
              <VideoForm initial={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
            ) : (
              <button data-testid="add-video-button" onClick={() => setEditing(EMPTY)}
                className="flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0A] font-semibold px-6 py-3 text-sm uppercase tracking-widest hover:bg-[#C9A227] transition-colors">
                <Plus className="w-4 h-4" /> Ajouter une vidéo
              </button>
            )}

            {videos.length === 0 ? (
              <p data-testid="admin-no-videos" className="text-[#8F8F8F] text-sm">Aucune vidéo dans le catalogue.</p>
            ) : (
              <div className="bg-[#141414] border border-white/10 divide-y divide-white/5">
                {videos.map((v) => (
                  <div key={v.id} data-testid={`admin-video-row-${v.id}`} className="flex items-center gap-4 p-5">
                    <div className="w-24 aspect-video bg-[#1A1A1A] overflow-hidden shrink-0 hidden sm:block">
                      {v.thumbnail_url && <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#F5F3EE] truncate">{v.title}</p>
                      <p className="text-xs text-[#8F8F8F] mt-1">
                        {formatPrice(v.price, v.currency)} · Vimeo ID: {v.vimeo_video_id || "—"}
                        {v.ott_product_id ? ` · Produit OTT: ${v.ott_product_id}` : ""}
                      </p>
                    </div>
                    <button data-testid={`edit-video-${v.id}`} onClick={() => setEditing(v)}
                      className="p-2 text-[#8F8F8F] hover:text-[#D4AF37] transition-colors"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`delete-video-${v.id}`} onClick={() => deleteVideo(v.id)}
                      className="p-2 text-[#8F8F8F] hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "access" && (
          <div className="mt-10 space-y-10">
            <form onSubmit={grantAccess} className="bg-[#141414] border border-white/10 p-6 flex flex-col sm:flex-row gap-4 sm:items-end" data-testid="grant-access-form">
              <div className="flex-1">
                <Label className="text-xs uppercase tracking-widest text-[#8F8F8F]">Email du client</Label>
                <Input data-testid="grant-email-input" type="email" value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)}
                  placeholder="client@email.com" className="mt-2 rounded-none border-white/15 bg-transparent text-[#F5F3EE] placeholder:text-[#5A5A5A]" />
              </div>
              <div className="flex-1">
                <Label className="text-xs uppercase tracking-widest text-[#8F8F8F]">Vidéo</Label>
                <select data-testid="grant-video-select" value={grantVideoId} onChange={(e) => setGrantVideoId(e.target.value)}
                  className="mt-2 w-full h-9 border border-white/15 bg-[#141414] px-3 text-[#F5F3EE] text-sm">
                  <option value="">— Choisir —</option>
                  {videos.map((v) => <option key={v.id} value={v.id}>{v.title}</option>)}
                </select>
              </div>
              <button type="submit" data-testid="grant-submit-button"
                className="bg-[#D4AF37] text-[#0A0A0A] font-semibold px-6 py-2.5 text-xs uppercase tracking-widest hover:bg-[#C9A227] transition-colors">
                Accorder l'accès
              </button>
            </form>

            {purchases.length === 0 ? (
              <p data-testid="admin-no-purchases" className="text-[#8F8F8F] text-sm">Aucun achat enregistré.</p>
            ) : (
              <div className="bg-[#141414] border border-white/10 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-left text-xs uppercase tracking-widest text-[#8F8F8F]">
                      <th className="p-4">Email</th><th className="p-4">Vidéo</th><th className="p-4">Source</th><th className="p-4">Date</th><th className="p-4"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {purchases.map((p) => (
                      <tr key={p.id} data-testid={`purchase-row-${p.id}`}>
                        <td className="p-4 text-[#F5F3EE]">{p.email}</td>
                        <td className="p-4 text-[#B0B0B0]">{p.video_title}</td>
                        <td className="p-4 text-[#8F8F8F]">{p.source === "vimeo_webhook" ? "Webhook Vimeo" : "Manuel (admin)"}</td>
                        <td className="p-4 text-[#8F8F8F]">{p.purchased_at ? new Date(p.purchased_at).toLocaleDateString("fr-FR") : ""}</td>
                        <td className="p-4">
                          <button data-testid={`revoke-${p.id}`} onClick={() => revoke(p.id)}
                            className="text-[#8F8F8F] hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {tab === "promos" && (
          <div className="mt-10 space-y-10">
            <form onSubmit={createPromo} className="bg-[#141414] border border-white/10 p-6 grid sm:grid-cols-5 gap-4 sm:items-end" data-testid="promo-form">
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#8F8F8F]">Code</Label>
                <Input data-testid="promo-code-field" value={promoForm.code}
                  onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                  placeholder="BIENVENUE10" className="mt-2 rounded-none border-white/15 bg-transparent text-[#F5F3EE] placeholder:text-[#5A5A5A] uppercase" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#8F8F8F]">Type</Label>
                <select data-testid="promo-type-select" value={promoForm.discount_type}
                  onChange={(e) => setPromoForm({ ...promoForm, discount_type: e.target.value })}
                  className="mt-2 w-full h-9 border border-white/15 bg-[#141414] px-3 text-sm text-[#F5F3EE]">
                  <option value="percent">Pourcentage (%)</option>
                  <option value="fixed">Montant fixe (€)</option>
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#8F8F8F]">Valeur</Label>
                <Input data-testid="promo-value-field" type="number" step="0.01" min="0.01" value={promoForm.value}
                  onChange={(e) => setPromoForm({ ...promoForm, value: e.target.value })}
                  placeholder={promoForm.discount_type === "percent" ? "10" : "2.00"}
                  className="mt-2 rounded-none border-white/15 bg-transparent text-[#F5F3EE] placeholder:text-[#5A5A5A]" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#8F8F8F]">Limite (opt.)</Label>
                <Input data-testid="promo-maxuses-field" type="number" min="1" value={promoForm.max_uses}
                  onChange={(e) => setPromoForm({ ...promoForm, max_uses: e.target.value })}
                  placeholder="∞" className="mt-2 rounded-none border-white/15 bg-transparent text-[#F5F3EE] placeholder:text-[#5A5A5A]" />
              </div>
              <button type="submit" data-testid="promo-create-button"
                className="bg-[#D4AF37] text-[#0A0A0A] font-semibold px-6 py-2.5 text-xs uppercase tracking-widest hover:bg-[#C9A227] transition-colors">
                Créer
              </button>
            </form>

            {promos.length === 0 ? (
              <p data-testid="admin-no-promos" className="text-[#8F8F8F] text-sm">Aucun code promo.</p>
            ) : (
              <div className="bg-[#141414] border border-white/10 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-left text-xs uppercase tracking-widest text-[#8F8F8F]">
                      <th className="p-4">Code</th><th className="p-4">Remise</th><th className="p-4">Utilisations</th><th className="p-4">Statut</th><th className="p-4"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {promos.map((p) => (
                      <tr key={p.id} data-testid={`promo-row-${p.id}`}>
                        <td className="p-4 text-[#D4AF37] font-medium">{p.code}</td>
                        <td className="p-4 text-[#F5F3EE]">{p.discount_type === "percent" ? `−${p.value}%` : `−${p.value.toFixed(2)} €`}</td>
                        <td className="p-4 text-[#8F8F8F]">{p.uses}{p.max_uses ? ` / ${p.max_uses}` : ""}</td>
                        <td className="p-4">
                          <button data-testid={`promo-toggle-${p.id}`} onClick={() => togglePromo(p.id)}
                            className={`text-xs uppercase tracking-widest px-3 py-1 border transition-colors ${p.active ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/20 text-[#8F8F8F]"}`}>
                            {p.active ? "Actif" : "Inactif"}
                          </button>
                        </td>
                        <td className="p-4">
                          <button data-testid={`promo-delete-${p.id}`} onClick={() => deletePromo(p.id)}
                            className="text-[#8F8F8F] hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
